import { Component, OnInit, Input, OnDestroy, forwardRef } from '@angular/core';
import { TemplateService } from '../../../../../services/template.service';
import { Number2textThService } from '../../../../../services/number2text-th.service';
import { AsyncValidatorFn,AbstractControl, ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { combineLatest, Observable,of,timer, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LineItem } from '../../document-issue.model';
import Decimal from 'decimal.js-light';


@Component({
  selector: 'app-line-item-form',
  templateUrl: './line-item-form.component.html',
  styleUrls: ['./line-item-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LineItemFormComponent),
      multi: true
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => LineItemFormComponent),
      multi: true
    },
    TemplateService,
    Number2textThService
  ]
})
export class LineItemFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  onChange: any = () => { };
  onTouched: any = () => { };

  @Input() documentType: string;
  @Input() isSubmitted: boolean;

  lineItemForm: FormGroup;
  subscription: Subscription;

  countries: any[];
  documentTypes: any[];

  isSellerHeadOffice = false;
  isBuyerHeadOffice = false;
  isDisableAddDiscount = false;
  vats = [
    { label: 'Vat 7%', value: '7' },
    { label: 'Vat 0%', value: '0' },
    { label: 'Non-Vat', value: 'non-vat' },
  ];

  vatValue = {
    '0': 0,
    '7': 0.07
  }

  receiptVats = [
    { label: 'Empty', value: '' },
    { label: 'Non-Vat', value: 'non-vat' },
    { label: 'Vat 0%', value: '0' },
    { label: 'Vat 7%', value: '7' }
  ];


  constructor(
    private formBuilder: FormBuilder,
    private template: TemplateService,
    private number2text_th: Number2textThService
  ) { }

  ngOnInit() {
    this.setDocumentTypes();

    this.createLineItemForm();
    this.lineItemForm.get('items').valueChanges.subscribe(()=>{this.calVAT()})
    this.lineItemForm.get('endBillDiscountList').valueChanges.subscribe(()=>{this.calVAT()})

    // any time the inner form changes update the parent of any change
    this.createSubscriptions();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  calVAT() {
    let vat = new Decimal(0);
    vat = vat.add(this.vatAmount.mul(this.vatValue['7']));
    this.vat.setValue(vat.toDecimalPlaces(3).toDecimalPlaces(2).toString())
  }

  writeValue(value: any) {
    if (value) {
      this.lineItemForm.setValue(value);
      this.onChange(value);
      this.onTouched();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // communicate the inner form validation to the parent form
  validate(_control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return new Promise(resolve =>
      this.lineItemForm.valid ? resolve(null) : resolve({ lineItemForm: { valid: false } })
    );
  }

  createSubscriptions() {
    // Subscribe to valueChanges and statusChanges
    this.subscription = combineLatest([this.lineItemForm.valueChanges, this.lineItemForm.statusChanges]).subscribe(
      () => {
        const calculateValue = {
          amountText: this.amountText,
          subTotal: this.subTotal.toFixed(2),
          discountTotal: this.discountTotal.toFixed(2),
          balance: this.balance.toFixed(2),
          vat: this.vat.value ? this.vat.value : '',
          nonVATAmount: this.nonVATAmount ? this.nonVATAmount.toFixed(2) : '',
          vat0Amount: this.vat0Amount ? this.vat0Amount.toFixed(2): '',
          vatAmount: this.vatAmount ? this.vatAmount.toFixed(2): '',
          netTotal: this.netTotal.toFixed(2),
          correctAmount: this.correctAmount.toFixed(2),
          endOfBillDiscount: this.endOfBillDiscount.toFixed(2),
          items: (this.items.value as LineItem[]).map((item, index) => ({
            productID: item.productID,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            discount: item.discount,
            total: this.getTotal(index).toFixed(2),
            lineVAT: item.lineVAT,
          })),
        }

        this.onChange(Object.assign({}, this.lineItemForm.getRawValue(), calculateValue));
        this.onTouched();
      });
  }

  setDocumentTypes() {
    this.documentTypes = this.template.getDropdown();

    if (this.isCreditNote || this.isDebitNote) {
      this.documentTypes.shift();
    } else {
      this.documentTypes = this.documentTypes.filter(element => element.value === this.documentType);
    }
  }

  get isCreditNote() {
    return this.documentType === 'credit_note';
  }

  get isDebitNote() {
    return this.documentType === 'debit_note';
  }

  get isReceipt() {
    return this.documentType === 'receipt';
  }

  createLineItemForm() {
    this.lineItemForm = this.formBuilder.group({
      items: this.formBuilder.array([
        this.formBuilder.group({
          productID: [null],
          description: [null, Validators.required],
          quantity: ['1', Validators.required],
          unit: [null],
          unitPrice: ['0.00', Validators.required],
          discount: [null],
          lineVAT:["7"]
        })
      ]),
      endBillDiscountList: this.formBuilder.array([
        this.formBuilder.group({
          endBillDiscount: [null],
          typeDiscount:['7']
        })
      ]),
      discountReason: [null],
      originalAmount: (this.isCreditNote || this.isDebitNote) ? [null, Validators.required] : [null],
      originalVAT: (this.isCreditNote || this.isDebitNote) ? [null, Validators.required] : [null],
      nonVATAmount: [null],
      vat0Amount:[null],
      vatAmount:[null],
      vat:[null],
      endOfBillDiscount: [null],
    }
    ,{ asyncValidator: this.validateNegative}
    );
  }

  validateNegative: AsyncValidatorFn = (control: FormGroup): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    const isNegative = Boolean(!(this.nonVATAmount.isNegative() || this.vat0Amount.isNegative() || this.vatAmount.isNegative()))
    return timer(300).pipe(
      switchMap(() => {
        return (isNegative) ? of(null) : new Promise(resolve => resolve({ vatAmountNegative:true }))
      })
    );
  };

  get items() {
    return this.lineItemForm.get('items') as FormArray;
  }

  get endBillDiscountList() {
    return this.lineItemForm.get('endBillDiscountList') as FormArray;
  }

  get discountReason() {
    return this.lineItemForm.get('discountReason');
  }

  get originalAmount() {
    return this.lineItemForm.get('originalAmount');
  }

  get originalVAT() {
    return this.lineItemForm.get('originalVAT');
  }

  get vat() {
    return this.lineItemForm.get('vat');
  }

  getProductID(index: number) {
    return this.items.controls[index].get('productID');
  }

  getDescription(index: number) {
    return this.items.controls[index].get('description');
  }

  getQuantity(index: number) {
    return this.items.controls[index].get('quantity');
  }

  getUnit(index: number) {
    return this.items.controls[index].get('unit');
  }

  getUnitPrice(index: number) {
    return this.items.controls[index].get('unitPrice');
  }

  getDiscount(index: number) {
    return this.items.controls[index].get('discount');
  }

  getLineVAT(index: number) {
    return this.items.controls[index].get('lineVAT');
  }

  getTypeDiscount(index: number) {
    return this.endBillDiscountList.controls[index].get('typeDiscount');
  }

  getEndBillDiscount(index: number) {
    return this.endBillDiscountList.controls[index].get('endBillDiscount');
  }

  getTotal(index: number) {
    const quantity = new Decimal(this.getQuantity(index).value || 0);
    const unitPrice = new Decimal(this.getUnitPrice(index).value || 0);
    const discount = new Decimal(this.getDiscount(index).value || 0);
    const total = quantity.mul(unitPrice).minus(discount);

    return total.toDecimalPlaces(2);
  }

  get endOfBillDiscount() {
    let endOfBillDiscount = new Decimal(0)
    for (let index = 0; index < this.endBillDiscountList.length; index++) {
      const discount = new Decimal(this.getEndBillDiscount(index).value || 0)
      endOfBillDiscount = endOfBillDiscount.add(discount);
    }
    return endOfBillDiscount.toDecimalPlaces(2);
  }

  get subTotal() {
    let subTotal = new Decimal(0);

    for (let index = 0; index < this.items.length; index++) {
      const quantity = new Decimal(this.getQuantity(index).value || 0);
      const unitPrice = new Decimal(this.getUnitPrice(index).value || 0);

      subTotal = subTotal.add(quantity.mul(unitPrice));
    }

    return subTotal.toDecimalPlaces(2);
  }

  get vatAmount(){
    let vatAmount = new Decimal(0);
    for (let index = 0; index < this.items.length; index++) {
      if (this.getLineVAT(index).value == '7'){
        vatAmount = vatAmount.add(this.getTotal(index));
      }
    }
    for (let index = 0; index < this.endBillDiscountList.length; index++) {
      if (this.getTypeDiscount(index).value == '7'){
        const discount = new Decimal(this.getEndBillDiscount(index).value || 0)
        vatAmount = vatAmount.minus(discount);
      }
    }
    return vatAmount.toDecimalPlaces(2)
  }
  get nonVATAmount() {
    let nonVATAmount = new Decimal(0);
    for (let index = 0; index < this.items.length; index++) {
      if (this.getLineVAT(index).value == 'non-vat'){
        nonVATAmount = nonVATAmount.add(this.getTotal(index));
      }
    }
    for (let index = 0; index < this.endBillDiscountList.length; index++) {
      if (this.getTypeDiscount(index).value == 'non-vat'){
        const discount = new Decimal(this.getEndBillDiscount(index).value || 0)
        nonVATAmount = nonVATAmount.minus(discount);
      }
    }
    return nonVATAmount.toDecimalPlaces(2);
  }

  get vat0Amount() {
    let vat0Amount = new Decimal(0);
    for (let index = 0; index < this.items.length; index++) {
      if (this.getLineVAT(index).value == '0') {
        vat0Amount = vat0Amount.add(this.getTotal(index));
      }
    }
    for (let index = 0; index < this.endBillDiscountList.length; index++) {
      if (this.getTypeDiscount(index).value == '0'){
        const discount = new Decimal(this.getEndBillDiscount(index).value || 0)
        vat0Amount = vat0Amount.minus(discount);
      }
    }
    return vat0Amount.toDecimalPlaces(2);
  }

  get discountTotal() {
    let discountTotal = new Decimal(0);

    for (let index = 0; index < this.items.length; index++) {
      const discount = new Decimal(this.getDiscount(index).value || 0);

      discountTotal = discountTotal.add(discount);
    }

    return discountTotal.toDecimalPlaces(2);
  }

  get balance() {
    const balance = this.subTotal.minus(this.discountTotal).minus(this.endOfBillDiscount);
    return balance.toDecimalPlaces(2);
  }

  get netTotal() {
    const vatValue = new Decimal(this.vat.value || 0)
    let netTotal = this.balance.add(vatValue)
    return netTotal.toDecimalPlaces(2);
  }

  get correctAmount() {
    let correctAmount = new Decimal(0);

    const originalAmount = new Decimal(this.originalAmount.value || 0);

    if (this.isDebitNote) {
      correctAmount = originalAmount.add(this.balance);
    } else if (this.isCreditNote) {
      correctAmount = originalAmount.minus(this.balance);
    }

    return correctAmount.toDecimalPlaces(2);
  }

  get amountText() {
    return this.number2text_th.currencyFormat_Baht(this.netTotal.toString());
  }

  addItem() {
    this.items.push(
      this.formBuilder.group({
        productID: [null],
        description: [null, Validators.required],
        quantity: ['1', Validators.required],
        unit: [null],
        unitPrice: ['0.00', Validators.required],
        discount: [null],
        lineVAT:["7"]
      })
    );
  }

  removeItem(index: number) {
    this.items.removeAt(index);
  }

  addEndBillDiscount() {
    this.endBillDiscountList.push(
      this.formBuilder.group({
        endBillDiscount: [null],
        typeDiscount:['7']
      })
    );
    if (this.endBillDiscountList.length >= 3) {
      this.isDisableAddDiscount = true
    }
  }

  removeEndBillDiscount(index: number) {
    this.endBillDiscountList.removeAt(index);
    if (this.endBillDiscountList.length < 3) {
      this.isDisableAddDiscount = false
    }
  }
}



// WEBPACK FOOTER //
// /opt/app/src/app/features/document/document-issue/components/line-item-form/line-item-form.component.ts